import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button, Modal, PageHeader, Pill, Table, Td, Th } from "@/components/ui";
import { ServerForm } from "@/components/ServerForm";
import { SshKeyModal } from "@/components/SshKeyModal";
import { ArchiveBanner } from "@/components/ArchiveBanner";

interface Server {
  id: number;
  name: string;
  host: string;
  port: number;
  server_type: string;
  max_users: number;
  max_connections: number;
  is_active: boolean;
  active_users: number;
  tier?: "standard" | "premium";
}

interface ServersResponse {
  servers: Server[];
}

interface SummaryItem {
  server_id: number;
  connections: number | null;
  reachable: boolean;
  active: boolean;
  ts: string | null;
}

interface SummaryResponse {
  items: SummaryItem[];
}

export default function Servers() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [sshKeyOpen, setSshKeyOpen] = useState(false);
  const q = useQuery<ServersResponse>({
    queryKey: ["servers"],
    queryFn: () => api("/api/admin/servers"),
  });

  const summary = useQuery<SummaryResponse>({
    queryKey: ["servers-summary"],
    queryFn: () => api("/api/admin/servers/metrics/summary"),
    refetchInterval: 30000,
  });

  const byServer = new Map<number, SummaryItem>();
  (summary.data?.items ?? []).forEach((i) => byServer.set(i.server_id, i));

  const servers = q.data?.servers ?? [];

  return (
    <div className="p-4 md:p-8">
      <PageHeader
        title="Серверы"
        right={
          <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
            <div className="text-zinc-400 text-sm">всего: {servers.length}</div>
            <Button variant="ghost" onClick={() => setSshKeyOpen(true)}>
              SSH ключ
            </Button>
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              + Добавить
            </Button>
          </div>
        }
      />
      <ArchiveBanner what="Серверы старой модели (Server)" />

      {q.isLoading && <div className="text-zinc-400">Загрузка…</div>}
      {q.isError && <div className="text-red-400">Ошибка загрузки</div>}

      {servers.length > 0 && (
        <Table>
          <thead>
            <tr>
              <Th>id</Th>
              <Th>имя</Th>
              <Th>host</Th>
              <Th>port</Th>
              <Th>тип</Th>
              <Th>загрузка</Th>
              <Th>коннекты</Th>
              <Th>статус</Th>
            </tr>
          </thead>
          <tbody>
            {servers.map((s) => {
              const pct = s.max_users > 0 ? Math.round((s.active_users / s.max_users) * 100) : 0;
              const summ = byServer.get(s.id);
              return (
                <tr
                  key={s.id}
                  className="hover:bg-zinc-900/50 cursor-pointer"
                  onClick={() => navigate(`/servers/${s.id}`)}
                >
                  <Td className="text-zinc-500">
                    <Link
                      to={`/servers/${s.id}`}
                      className="text-brand hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      #{s.id}
                    </Link>
                  </Td>
                  <Td className="text-zinc-100">{s.name}</Td>
                  <Td className="font-mono text-zinc-300">{s.host}</Td>
                  <Td>{s.port}</Td>
                  <Td>
                    <Pill tone={s.server_type === "paid" ? "success" : "neutral"}>
                      {s.server_type}
                    </Pill>
                  </Td>
                  <Td>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${pct > 80 ? "bg-red-500" : pct > 50 ? "bg-amber-500" : "bg-emerald-500"}`}
                          style={{ width: `${Math.min(100, pct)}%` }}
                        />
                      </div>
                      <span className="text-zinc-400 text-xs">
                        {s.active_users}/{s.max_users}
                      </span>
                    </div>
                  </Td>
                  <Td>
                    {summ ? (
                      <span
                        className={
                          summ.reachable && summ.active
                            ? "font-mono text-emerald-300"
                            : "font-mono text-zinc-500"
                        }
                      >
                        {(summ.connections ?? 0).toLocaleString("ru-RU")}
                      </span>
                    ) : (
                      <span className="text-zinc-600">—</span>
                    )}
                  </Td>
                  <Td>
                    {s.is_active ? (
                      <Pill tone="success">активен</Pill>
                    ) : (
                      <Pill tone="danger">неактивен</Pill>
                    )}
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      <SshKeyModal open={sshKeyOpen} onClose={() => setSshKeyOpen(false)} />

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Добавить сервер"
      >
        <ServerForm
          mode="create"
          onSaved={(created) => {
            setCreateOpen(false);
            qc.invalidateQueries({ queryKey: ["servers"] });
            navigate(`/servers/${created.id}`);
          }}
          onCancel={() => setCreateOpen(false)}
        />
      </Modal>
    </div>
  );
}
